/** @jsxImportSource @gongxigames/framework/core/fragments/BuilderY */
/** @jsxRuntime automatic */
import {Container} from 'pixi.js';
import {ITrackEntry, IEvent} from 'pixi-spine';
import {
    AnimationProperty,
    Button, CoreDI, Easing, inversifyContainer, Meter, ObjectBuilder,
    RichText,
    Spine, Store,
    UpdateManager
} from '@gongxigames/framework/core';
import {
    GenericPopupConfig,
    GenericPopupCounterValue,
    GenericPopupData, PopupSpineConfig
} from '@gongxigames/framework/slot/types';
import {DEFAULT_FADE_DURATION} from '@gongxigames/framework/slot/modules/genericPopup/fader';
import {slotStore} from '@gongxigames/framework/slotV2/globals';
import {$Container, PointLike} from '@gongxigames/framework/core/fragments/BuilderY';
import {$FullScreenFader} from '@gongxigames/framework/slotV2/JSX/FullScreenFader';
import {$FullScreenFit} from '@gongxigames/framework/slotV2/JSX/FullScreenFit';

type GenericPopupViewOptions = {
    onContinueButtonClicked: () => void;
    onClosed: () => void;
    onFadeOutStarted: () => void;
};

export class GenericPopupViewV2 {

    protected _objectBuilder!: ObjectBuilder;

    protected _continueButton?: Button;
    protected _spineContainer!: Container;
    protected _spineUpdateManager!: UpdateManager;
    public container!: Container;

    protected _spines: Spine[] = [];
    protected _config!: GenericPopupConfig;
    protected data?: GenericPopupData;

    protected _counter?: RichText;
    protected _meter?: Meter;

    protected showPopup = Store.signal(false);
    protected width = Store.signal(3840);
    protected height = Store.signal(2160);

    constructor(protected options: GenericPopupViewOptions) {
        this._spineContainer = new Container();
        this.createMainScene();
        this._spines = [];
        this._spineUpdateManager = new UpdateManager();
        this._spineUpdateManager.start();
        slotStore.updateManager.value!.add(this._spineUpdateManager);
    }

    protected createMainScene(): void {
        const offset = Store.computed<PointLike>(() => {
            return {
                x: this.width.value / 2,
                y: this.height.value / 2
            };
        });

        this.container = <$Container name='popup' visible={this.showPopup}>
            <$FullScreenFader
                opacity={0.5}
                show={true}
                interactive={!this._continueButton}
                buttonMode={!this._continueButton}
                events={{pointertap: () => this.onContinueButtonClicked()}}
            />
            <$FullScreenFit width={this.width} height={this.height} offset={offset}>
                {this._spineContainer}
            </$FullScreenFit>
        </$Container> as Container;
    }

    public onContinueButtonClicked(): void {
        this.disableContinueButton();
        this.playContinueButtonSound();
        this.options.onContinueButtonClicked();
    }

    public disableContinueButton(): void {
        if (this._continueButton && this._continueButton.enabled) {
            this._continueButton.disable();
        }
    }

    public initPopup(config: GenericPopupConfig, data?: GenericPopupData): void {
        this.config = config;
        this.data = data;
        this.initSpines();

        this.width.value = config.size!.width;
        this.height.value = config.size!.height;
    }

    public playStart(): void {
        this.show();
        this.playShowAnimation();
        this.setSpinesSlots();
    }

    public playFadeOutAnimation(): void {
        const fadeDuration = this._config.fader?.fadeDuration || DEFAULT_FADE_DURATION;

        slotStore.updateManager.value!.addTween(this.container)
            .to({alpha: 0.0}, fadeDuration)
            .onComplete(this.onFadeOutAnimationFinished.bind(this))
            .start();

        this.options.onFadeOutStarted();
    }

    protected initMeter(value: GenericPopupCounterValue): void {
        if (this._counter) {
            this._meter = new Meter(this._counter, Easing.Linear.None, slotStore.currencyFormatter.value!);
            this._meter.start();
            this._meter.from(value.from || 0).to(value.to, value.duration || 0);

            slotStore.updateManager.value!.add(this._meter);
        }
    }

    protected showSpines(): void {
        this._spines.forEach(spine => spine.visible = true);
    }

    protected playShowAnimation(): void {
        const promises = this._config.spine.map(spineConfig => this.playStartAnimation(spineConfig));

        Promise.all(promises).then(this.showAnimationFinished.bind(this));
    }

    protected hide(): void {
        this.showPopup.value = false;
        this._spines.forEach(spine => this._spineUpdateManager.remove(spine));
        this._spineContainer.removeChildren();
        this._spines.length = 0;
        this._continueButton = undefined;
        this.options.onClosed();
    }

    protected show(): void {
        this.showPopup.value = true;
        this.showSpines();
    }

    protected playStartAnimation(spineConfig: PopupSpineConfig): Promise<any> | undefined {
        const {name} = spineConfig.spine;
        const {start, skin} = spineConfig.animation!;

        if (start && typeof start === 'string') {
            const getAnimationProperty = (resolve: (value: any) => void) => {
                const completeCallBack = this.getStartAnimationCompleteCallBack(resolve);
                const eventCallBack = this.getStartAnimationEventCallBack(spineConfig);

                return this.getStartAnimationProperty(start, skin, completeCallBack, eventCallBack);
            };

            return new Promise(resolve => this.playAnimationInSpine(name, getAnimationProperty(resolve)));
        }
    }

    /**
     * Can be overridden
     */
    protected getStartAnimationCompleteCallBack(resolve: (value: any) => void): (value: any) => void {
        return resolve;
    }

    protected getStartAnimationEventCallBack(spineConfig: PopupSpineConfig): (entry: ITrackEntry, event: IEvent) => void {
        return this.applyButtonEnableEvent(spineConfig);
    }

    protected applyButtonEnableEvent(spineConfig: PopupSpineConfig): (entry: ITrackEntry, event: IEvent) => void {
        const enableEvent = spineConfig.continueButton?.enableEvent;

        return (entry: ITrackEntry, event: IEvent) => {
            if (enableEvent && event.data.name === enableEvent) {
                this._continueButton?.enable();
            }
        };
    }

    protected getStartAnimationProperty(
        animationName: string,
        skinName: string | undefined,
        complete: (entry: ITrackEntry) => void,
        event: (entry: ITrackEntry, event: IEvent) => void = () => {
        }
    ): AnimationProperty {
        return {animationName, listeners: {complete, event}, skinName};
    }

    public playWaitingSound(): void {
        const {waiting} = this.config.soundConfig;

        this.playSound(waiting);
    }

    public playTransitionEnterSound(): void {
        const {transitionEnter} = this.config.soundConfig;

        this.playSound(transitionEnter);
    }

    public playExitSound(): void {
        const {exit} = this.config.soundConfig;

        this.playSound(exit);
    }

    public playContinueButtonSound(): void {
        const {buttonClickSound} = this.config.soundConfig;

        this.playSound(buttonClickSound);
    }

    protected playSound(sound?: string): void {
        if (sound) {
            slotStore.soundPlayer.value!.play(sound);
        }
    }

    protected initSpines(): void {
        this._config.spine.forEach(spineConfig => this.initSpine(spineConfig));
    }

    protected initSpine(spineConfig: PopupSpineConfig): void {
        const spine = this.objectBuilder.buildSpineWithConfig(spineConfig.spine);

        if (spine) {
            spine.name = spineConfig.spine.name;
            this.setPopupSpine(spine);
        }
    }

    protected setPopupSpine(spine: Spine): void {
        spine.visible = false;

        spine.start();

        this._spineUpdateManager.add(spine);
        this._spineContainer.addChild(spine);
        slotStore.spineUtils.value!.applyMasks(spine);
        this._spines.push(spine);
    }

    protected setContinueButton(spineConfig: PopupSpineConfig, spine: Spine): void {
        const {continueButton} = spineConfig;

        if (continueButton) {
            this._continueButton = this.objectBuilder.setUpSlot(continueButton, spine) as Button;
            this._continueButton.callback = this.onContinueButtonClicked.bind(this);
            this._continueButton.disable();
        }
    }

    protected setPopupValue(spineConfig: PopupSpineConfig, spine: Spine): void {
        const {value} = spineConfig;

        if (value) {
            this.objectBuilder.setUpSlot(value, spine);
        }
    }

    protected setPopupCounter(spineConfig: PopupSpineConfig, spine: Spine): void {
        const {counter} = spineConfig;

        if (counter) {
            this._counter = this.objectBuilder.setUpSlot(counter, spine) as RichText;
        }
    }

    protected onFadeOutAnimationFinished(): void {
        this.hide();
        this.container.alpha = 1;
    }

    protected setSpinesSlots(): void {
        const checkOldSpineStructure = (spineConfig: PopupSpineConfig, value: string) => {
            if (spineConfig.value) {
                const {text, richText} = spineConfig.value;

                if (richText) {
                    richText.values = {value};
                } else if (text) {
                    text.text = value;
                }
            }
        };

        this._config.spine.forEach(spineConfig => {
            const spine = this.getSpineByName(spineConfig.spine.name);

            if (spine) {
                if (this.data?.counterValue) {
                    checkOldSpineStructure(spineConfig, this.data.counterValue.to.toString());
                }
                this.setContinueButton(spineConfig, spine);
                this.setPopupValue(spineConfig, spine);
                this.setPopupCounter(spineConfig, spine);

                if (this.data?.counterValue) {
                    this.initMeter(this.data.counterValue);
                }
            }
        });
    }

    protected showAnimationFinished(): void {
        if (this._continueButton && !this._continueButton.enabled) {
            this._continueButton.enable();
        }

        this.playIdleAnimation();
    }

    protected playIdleAnimation(): void {
        this._config.spine.forEach(spineConfig => {
            const {idle} = spineConfig.animation!;
            const {name} = spineConfig.spine;

            if (idle && typeof idle === 'string') {
                this.playAnimationInSpine(name, this.getIdleAnimationProperty(idle));
            }
        });
    }

    protected getIdleAnimationProperty(animationName: string): AnimationProperty {
        return {animationName, loop: true};
    }

    protected playAnimationInSpine(spineName: string, animationParam: AnimationProperty): void {
        this._spines.forEach(spine => {
            if (spine.name === spineName) {
                spine.setAnimation(animationParam);
            }
        });
    }

    protected getSpineByName(spineName: string): Spine | undefined {
        return this._spines.find(spine => spine.name === spineName);
    }

    public set config(value: GenericPopupConfig) {
        this._config = value;
    }

    public get config(): GenericPopupConfig {
        return this._config;
    }

    public get continueButton(): Button {
        return this._continueButton!;
    }

    public get objectBuilder(): ObjectBuilder {
        this._objectBuilder ??= inversifyContainer.get<ObjectBuilder>(CoreDI.ObjectBuilder);

        return this._objectBuilder;
    }
}
